// [xleaders] XO-28: mark `textColor` cho ô trả lời Zalo cá nhân. `@chatwoot/prosemirror-schema` là
// package npm không có mark này, nên schema / parser / serializer / nút menu được bọc ở đây và chỉ
// kích hoạt khi registry FORMATTING của kênh liệt kê `textColor` (dashboard/constants/editor.js) —
// kênh khác đi nguyên đường của package.
import MarkdownIt from 'markdown-it';
import { Schema } from 'prosemirror-model';
import { MarkdownParser, MarkdownSerializer } from 'prosemirror-markdown';
import { menuBar } from 'prosemirror-menu';
import {
  buildMessageSchema,
  MessageMarkdownTransformer,
  MessageMarkdownSerializer,
  toggleMark,
} from '@chatwoot/prosemirror-schema';
import buildMenuOptions from '@chatwoot/prosemirror-schema/src/menu/menuOptions';
import { cmdItem } from '@chatwoot/prosemirror-schema/src/menu/common';
import zaloTextColorPlugin, {
  ZALO_TEXT_COLORS,
  zaloColorHref,
  textColorStyle,
} from 'shared/helpers/xleaders/zaloTextColor';

export const TEXT_COLOR_MARK = 'textColor';

const RGB_REGEX = /^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/;

// Dán từ nơi khác: trình duyệt có thể trả `#db342e` hoặc `rgb(219, 52, 46)`; màu ngoài bảng Zalo bỏ.
const hexFromCss = value => {
  const rgb = value.match(RGB_REGEX);
  const hex = rgb
    ? rgb
        .slice(1)
        .map(part => Number(part).toString(16).padStart(2, '0'))
        .join('')
    : value.replace(/^#/, '');
  return ZALO_TEXT_COLORS.some(color => color.hex === hex) ? hex : null;
};

const textColorMarkSpec = {
  attrs: { color: {} },
  // Màu và link cùng một đoạn chữ thì serializer ra link lồng link `[[a](zalo-color:x)](url)` —
  // markdown không hiểu, ký hiệu lộ ở bong bóng lẫn Zalo. Ticket chốt không làm màu ở link: bấm màu
  // lên link thì link rớt, autolink lên chữ đã có màu thì không tạo link (luật `Mark.addToSet`).
  excludes: 'textColor link', // khai excludes là mất mặc định tự loại chính nó — phải ghi lại
  parseDOM: [
    {
      style: 'color',
      getAttrs: value => {
        const hex = hexFromCss(value.trim().toLowerCase());
        return hex ? { color: hex } : false;
      },
    },
  ],
  toDOM: mark => ['span', { style: textColorStyle(mark.attrs.color) }, 0],
};

// `buildMessageSchema` của package chỉ biết các mark có sẵn của nó, nên dựng schema với phần còn
// lại rồi nối `textColor` vào bảng mark và vào danh sách mark mà paragraph / list_item / blockquote
// cho phép (`marks` là chuỗi tên cách nhau bằng khoảng trắng; code_block để trống có chủ ý).
export function buildXleadersMessageSchema(marks, nodes) {
  if (!marks.includes(TEXT_COLOR_MARK)) return buildMessageSchema(marks, nodes);

  const base = buildMessageSchema(
    marks.filter(mark => mark !== TEXT_COLOR_MARK),
    nodes
  ).spec;
  const nodeSpecs = {};
  base.nodes.forEach((name, spec) => {
    nodeSpecs[name] =
      typeof spec.marks === 'string' && spec.marks
        ? { ...spec, marks: `${spec.marks} ${TEXT_COLOR_MARK}` }
        : spec;
  });
  return new Schema({
    nodes: nodeSpecs,
    marks: base.marks.addToEnd(TEXT_COLOR_MARK, textColorMarkSpec),
  });
}

// Cùng cấu hình với tokenizer trong `schema/markdown/messageParser.js` của package (không export),
// cộng plugin đổi link màu thành token `zalo_color_*`.
const tokenizer = MarkdownIt('commonmark', { html: false, linkify: false });
tokenizer.enable(['entity', 'escape']);
tokenizer.disable(['table', 'hr', 'heading', 'lheading'], true);
tokenizer.use(zaloTextColorPlugin);

export function createXleadersMarkdownTransformer(schema) {
  if (!schema.marks[TEXT_COLOR_MARK]) {
    return new MessageMarkdownTransformer(schema);
  }
  const transformer = new MessageMarkdownTransformer(schema, tokenizer);
  transformer.markdownParser = new MarkdownParser(schema, tokenizer, {
    ...transformer.markdownParser.tokens,
    zalo_color: {
      mark: TEXT_COLOR_MARK,
      getAttrs: token => ({ color: token.meta.color }),
    },
  });
  return transformer;
}

const textColorSerializer = {
  open: '[',
  close: (_state, mark) => `](${zaloColorHref(mark.attrs.color)})`,
  mixable: true,
  expelEnclosingWhitespace: true,
};

const serializerWithTextColor = new MarkdownSerializer(
  MessageMarkdownSerializer.nodes,
  {
    ...MessageMarkdownSerializer.marks,
    [TEXT_COLOR_MARK]: textColorSerializer,
  },
  MessageMarkdownSerializer.options
);

export function markdownSerializerFor(schema) {
  return schema.marks[TEXT_COLOR_MARK]
    ? serializerWithTextColor
    : MessageMarkdownSerializer;
}

const activeColor = (state, markType) => {
  const { from, to, empty, $from } = state.selection;
  if (empty) {
    return markType.isInSet(state.storedMarks || $from.marks())?.attrs.color;
  }
  let found = null;
  state.doc.nodesBetween(from, to, node => {
    if (node.isInline && !found) found = markType.isInSet(node.marks);
  });
  return found?.attrs.color;
};

// `toggleMark` của prosemirror-commands chỉ nhìn LOẠI mark: đang đỏ mà bấm cam thì nó bỏ màu thay vì
// đổi sang cam, sale phải bấm hai lần. Ở đây: cùng màu → bỏ, khác màu / chưa có → đặt (addMark tự thay
// mark cùng loại).
export const toggleTextColor = (markType, hex) => (state, dispatch) => {
  if (activeColor(state, markType) === hex) {
    return toggleMark(markType)(state, dispatch);
  }
  if (dispatch) {
    const mark = markType.create({ color: hex });
    const { from, to, empty } = state.selection;
    const tr = empty
      ? state.tr.addStoredMark(mark)
      : state.tr.addMark(from, to, mark).scrollIntoView();
    dispatch(tr);
  }
  return true;
};

const SVG_NS = 'http://www.w3.org/2000/svg';

const swatch = hex => {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  const circle = document.createElementNS(SVG_NS, 'circle');
  circle.setAttribute('cx', '12');
  circle.setAttribute('cy', '12');
  circle.setAttribute('r', '8');
  circle.setAttribute('fill', `#${hex}`);
  svg.appendChild(circle);
  return svg;
};

const colorItem = (markType, { hex, title }) =>
  cmdItem(toggleTextColor(markType, hex), {
    title,
    icon: { dom: swatch(hex) },
    class: 'xleaders-text-color',
    active: state => activeColor(state, markType) === hex,
    enable: true,
  });

// Các nút của package đứng trước / sau vị trí `textColor` trong `menu` của registry, bốn nút màu chèn
// vào đúng chỗ đó.
export function buildXleadersMenuContent(schema, enabledMenuOptions, methods) {
  const at = enabledMenuOptions.indexOf(TEXT_COLOR_MARK);
  const packageItems = keys =>
    buildMenuOptions(schema, { ...methods, enabledMenuOptions: keys })[0];
  return [
    ...packageItems(
      at < 0 ? enabledMenuOptions : enabledMenuOptions.slice(0, at)
    ),
    ...ZALO_TEXT_COLORS.map(color =>
      colorItem(schema.marks[TEXT_COLOR_MARK], color)
    ),
    ...packageItems(at < 0 ? [] : enabledMenuOptions.slice(at + 1)),
  ];
}

// `buildEditor` của package dựng menubar bên trong với danh sách nút cố định, không nhận nút lạ.
// Với schema có `textColor`: thanh menu thật đi qua `plugins`, còn menubar của package nhận danh
// sách rỗng — CSS của Editor.vue giấu `.ProseMirror-menubar:not(:has(*))` nên không để lại vệt.
export function xleadersEditorOptions(options) {
  const {
    schema,
    enabledMenuOptions = [],
    plugins = [],
    methods = {},
  } = options;
  if (!schema.marks[TEXT_COLOR_MARK]) return options;

  const content = buildXleadersMenuContent(schema, enabledMenuOptions, methods);
  return {
    ...options,
    plugins: [...plugins, menuBar({ floating: true, content: [content] })],
    enabledMenuOptions: [],
  };
}
